import type { MaybeRefOrGetter } from 'vue';
import type { CompositePrimaryKey, ResolvedMonospacePrimitiveField, ResolvedMonospaceSingleRelationField } from '#layers/data-model/public';
import { useOverlay } from '@nuxt/ui/composables';
import { toValue } from 'vue';
import { isEmptyKey, pickByPrimitiveFields } from '#layers/data-model/public';
import { LazyCollectionListingDrawer } from '#layers/listing/lazy';

/** Inputs shared by relation blocks that let a user pick one related record. */
export interface UseM2OSelectionOptions {
	/** Must be resolved before `handleSelect` runs. */
	field: MaybeRefOrGetter<ResolvedMonospaceSingleRelationField | null>;
	referenceKeyFields: MaybeRefOrGetter<ResolvedMonospacePrimitiveField[]>;
	/** Pending or saved key the drawer preselects; ignored while creating. */
	effectiveKey: MaybeRefOrGetter<CompositePrimaryKey | null>;
	isCreate: MaybeRefOrGetter<boolean>;
	/** Saved key; the only key ever passed to `emitDisconnect`. */
	referencedItemKey: MaybeRefOrGetter<CompositePrimaryKey | null>;
	emitConnect: (key: CompositePrimaryKey) => void;
	emitDisconnect: (key: CompositePrimaryKey) => void;
}

/** Opens the shared single-record picker and routes its selection to relation field handlers. */
export function useM2OSelection(options: UseM2OSelectionOptions) {
	const overlay = useOverlay();

	/** Open picker, connect first selected row, or disconnect persisted row after explicit clear. */
	async function handleSelect() {
		const modal = overlay.create(LazyCollectionListingDrawer, {
			destroyOnClose: true,
			props: {
				collectionId: toValue(options.field)!.oppositeCollectionId,
				selection: !toValue(options.isCreate) && !isEmptyKey(toValue(options.effectiveKey)) ? [toValue(options.effectiveKey)!] : [],
				selectionMode: 'single',
				selectionKeys: toValue(options.referenceKeyFields),
			},
		});

		const result = await modal.open().result;

		if (result) {
			if (result.selection.length > 0) {
				options.emitConnect(pickByPrimitiveFields(result.selection[0]!, toValue(options.referenceKeyFields)));
			}
			else if (result.selection.length === 0 && toValue(options.referencedItemKey)) {
				options.emitDisconnect(toValue(options.referencedItemKey)!);
			}
		}
	}

	/** Disconnect the currently persisted related record when one exists. */
	function handleDeselect() {
		const referencedItemKey = toValue(options.referencedItemKey);

		if (referencedItemKey) {
			options.emitDisconnect(referencedItemKey);
		}
	}

	return { handleSelect, handleDeselect };
}
